/**
 * Declaration of Accredited Investor Status Signing Component
 * Handles document viewing and signing with optional liveness verification
 */

import { FC, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Center, Loader, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { toast } from "react-toastify";
import DocViewer from "@cyntler/react-doc-viewer";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

import { Button } from "@funded-here-interface/common";
import { setRegtankLiveness } from "@funded-here-interface/common/src/features/auth/authSlice_Investor";
import {
  LivenessWebVerifyStatusEnum,
  EntityTypeEnum,
  KYCStatus,
} from "@funded-here-interface/common/src/constant/enum";
import { isValidRegtankUrl } from "@funded-here-interface/common/src/Utils/urlValidation";
import useGetUser from "@funded-here-interface/common/src/hooks/useGetUser";
import { useDocumentVisibility } from "@funded-here-interface/common/src/hooks/useDocumentVisibility";

import { RootState } from "../../store";
import { WEB_DOMAIN } from "../../constants/domain";
import {
  getBaseUrl,
  Microservice,
} from "@funded-here-interface/common/src/constant/baseUrl";
import { ThanksPageType } from "../../constants/enums";
import { useDeclarationOfAISign } from "../../hooks/useDocuSign";
import useSignedDocument from "../../hooks/useSignedDocument";
import useSignedDeclarationOfAI from "../../hooks/useSignedDeclarationOfAI";
import useCompleteOnboarding from "../../hooks/useCompleteOnboarding";
import useGetLivenessWebVerifyLink from "../../hooks/useGetLivenessWebVerifyLink";
import useGetRegtankLivenessWebVerifyStatus from "../../hooks/useGetRegtankLivenessWebVerifyStatus";
import { useRegtankLivenessSession } from "../../hooks/useRegtankLivenessSession";
import { useRegtankLivenessValidation } from "../../hooks/useRegtankLivenessValidation";
import { navigateToThanksPage } from "../ThanksPage/ThanksPageUtils";

import { useStyle } from "./DeclarationAccreditedInvestor.styles";
import {
  useDocumentState,
  useButtonState,
  FailedLivenessModal,
  COLORS,
  MESSAGES,
} from "../../components/DocumentSigning";

const AGREEMENT_NAME = "Investor Agreement" as const;

const DeclarationAccreditedInvestor: FC = () => {
  const location = useLocation();
  const { classes } = useStyle();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux state
  const {
    token,
    entityType,
    regtankRequest,
    regtankLiveness,
    regtankLivenessFeatureFlag,
    regtankOnboardingFeatureFlag,
    regtankOnboarding,
    hasEmailHistory,
    kycStatus,
    hasSignedDocument,
  } = useSelector((state: RootState) => state.auth);
  // API hooks
  const declarationOfAIMutation = useDeclarationOfAISign();
  const signedDocumentMutation = useSignedDocument();
  const signedDeclarationOfAIMutation = useSignedDeclarationOfAI();
  const completeOnboardingMutation = useCompleteOnboarding();
  const livenessLinkMutation = useGetLivenessWebVerifyLink();
  const livenessStatusMutation = useGetRegtankLivenessWebVerifyStatus();
  const { data, refetch } = useGetUser(token);
  const isPendingResubmission =
    kycStatus === KYCStatus.PENDING_RESUBMISSION && hasEmailHistory === true;

  // Local state
  const [regtankLivenessUrl, setRegtankLivenessUrl] = useState("");
  const [openedFailedLiveness, modalAction] = useDisclosure();

  // Custom hooks
  const documentState = useDocumentState();
  const livenessSession = useRegtankLivenessSession(
    documentState.documentId,
    data?.user?.regtankLiveness || regtankLiveness
  );
  const livenessValidation = useRegtankLivenessValidation(livenessSession);
  const buttonState = useButtonState({
    regtankLivenessFeatureFlag: regtankLivenessFeatureFlag ?? null,
    regtankOnboardingFeatureFlag: regtankOnboardingFeatureFlag ?? null,
    regtankOnboarding: regtankOnboarding ?? null,
    regtankRequest,
    isDisabledByLiveness: livenessValidation.isDisabled,
  });

  // Document viewer config
  const docs = [{ uri: documentState.realUrl, fileType: "pdf" as const }];
  const redirectUrl = window.location.href;

  /**
   * Generate Declaration of Accredited Investor document
   */
  const handleGenerateDocument = async () => {
    toast.info(MESSAGES.LOADING_DOC, { toastId: "loading-doc-ai" });

    try {
      declarationOfAIMutation.mutate(
        {
          token,
          webDomain: WEB_DOMAIN,
          redirectUrl: `${getBaseUrl(Microservice.DOCUMENT_MANAGEMENT)}/document-management/docusign/webhook`,
          isPendingResubmission,
        },
        {
          onSuccess: async (data) => {
            await refetch();
            documentState.updateDocument(
              data.url,
              data.documentId,
              data.completedAt || ""
            );
            documentState.incrementViewerKey();
          },
          onError: (e) => {
            toast.error((e as Error).message);
          },
        }
      );
    } catch (ex) {
      toast.error("Error generating Declaration of Accredited Investor");
    }
  };

  /**
   * Navigate to next page based on entity type
   */
  const navigateNext = () => {
    signedDeclarationOfAIMutation.mutate(
      { token },
      {
        onSuccess: () => {
          if (entityType === EntityTypeEnum.INSTITUTION) {
            completeOnboardingMutation.mutate(
              { token },
              {
                onSuccess: () => {
                  navigate("/note-offer-room");
                },
                onError: (e) => {
                  toast.error((e as Error).message);
                },
              }
            );
          } else {
            if (hasEmailHistory) {
              navigateToThanksPage(
                navigate,
                ThanksPageType.RESUBMITTED_IND_INVESTOR
              );
            } else {
              navigateToThanksPage(navigate, ThanksPageType.AGREEMENT);
            }
          }
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  };

  /**
   * Sign the document
   */
  const handleSign = () => {
    documentState.clearUrl();

    signedDocumentMutation.mutate(
      { documentId: documentState.documentId, token },
      {
        onSuccess: (data) => {
          sessionStorage.removeItem("regtankSessionId");
          toast.success(MESSAGES.SIGNING_SUCCESS);
          documentState.updateDocument(
            data.url,
            data.documentId,
            data.completedAt
          );
          documentState.incrementViewerKey();
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  };

  /**
   * Handle liveness verification or direct signing
   */
  const handleLivenessVerify = () => {
    buttonState.disableButton("");

    if (!regtankLivenessFeatureFlag) {
      handleSign();
      return;
    }

    // Use existing liveness URL if available
    if (regtankLivenessUrl) {
      if (!isValidRegtankUrl(regtankLivenessUrl)) {
        toast.error(MESSAGES.INVALID_URL);
        return;
      }
      window.open(regtankLivenessUrl, "_self");
      return;
    }

    // Generate new liveness URL
    toast.info(MESSAGES.LOADING_LIVENESS, {
      toastId: "loading-regtank-liveness-check-url",
    });

    livenessLinkMutation.mutate(
      {
        token,
        documentId: documentState.documentId,
        redirectUrl,
        agreementName: "Declaration of AI Status",
      },
      {
        onSuccess: (data) => {
          if (!isValidRegtankUrl(data.verifyLink)) {
            toast.error(MESSAGES.INVALID_URL);
            return;
          }

          sessionStorage.setItem("regtankSessionId", data.sessionId);

          dispatch(
            setRegtankLiveness({
              regtankLiveness: [
                {
                  id: documentState.documentId.toString(),
                  clientSessionId: documentState.documentId.toString(),
                  sessionId: data.sessionId,
                  livenessStatus: null,
                  regtankLivenessUrl: data.verifyLink,
                  noOfAttemptLiveness: 0,
                  reattemptApproval: null,
                  expiredDate: data.expiredDate,
                },
              ],
            })
          );

          window.open(data.verifyLink, "_self");
          documentState.incrementViewerKey();
        },
        onError: (e) => {
          toast.error((e as Error).message);
          documentState.incrementViewerKey();
        },
      }
    );
  };

  /**
   * Handle modal continue action
   */
  const handleContinue = () => {
    buttonState.disableButton(MESSAGES.VERIFICATION_DISABLED);
    modalAction.close();
    sessionStorage.removeItem("regtankSessionId");
    documentState.incrementViewerKey();
  };

  // Initial load: generate document
  useEffect(() => {
    handleGenerateDocument();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Generate document when page becomes visible again after switching tabs or inactive for a while
  useDocumentVisibility(
    () => documentState.incrementViewerKey(),
    !!documentState.documentId
  );

  // Verify liveness status when needed
  useEffect(() => {
    if (!livenessSession || !livenessValidation.shouldVerify) return;

    livenessStatusMutation.mutate(
      { token, sessionId: livenessSession.sessionId },
      {
        onSuccess: (data: any) => {
          if (data === LivenessWebVerifyStatusEnum.SUCCESS) {
            handleSign();
          } else if (
            data === LivenessWebVerifyStatusEnum.LIVENESS_FAILED ||
            data === LivenessWebVerifyStatusEnum.FAILED
          ) {
            modalAction.open();
          }
          documentState.incrementViewerKey();
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [livenessSession, livenessValidation.shouldVerify, token]);

  // Set liveness URL when user can attempt verification
  useEffect(() => {
    if (livenessValidation.canAttempt) {
      setRegtankLivenessUrl(livenessSession?.regtankLivenessUrl || "");
    }
  }, [livenessValidation.canAttempt, livenessSession]);

  return (
    <>
      <div className={classes.tempWideDisplay}>
        <div className={classes.topDiv}>
          <p className={classes.headinfo}>{AGREEMENT_NAME}</p>
        </div>

        {documentState.isDocumentLoaded && (
          <>
            <DocViewer
              documents={docs}
              key={documentState.viewerKey}
              className={classes.docviewer}
              style={{ width: "100%", height: 600, overflow: "auto" }}
              config={{
                header: {
                  disableHeader: false,
                  disableFileName: true,
                  retainURLParams: false,
                },
                pdfVerticalScrollByDefault: true,
              }}
            />

            {!documentState.isDocumentSigned && (
              <div className={classes.buttonContainer}>
                <Text style={{ marginBottom: 10, fontSize: 14 }}>
                  {MESSAGES.ACKNOWLEDGEMENT}
                </Text>
                <Button
                  disabled={buttonState.buttonDisabled}
                  onClick={handleLivenessVerify}
                  textColor="white"
                  backgroundColor={COLORS.PRIMARY}
                  title={buttonState.titleButton}
                >
                  Proceed to Sign
                </Button>
              </div>
            )}

            {documentState.isDocumentSigned && (
              <div className={classes.buttonContainer}>
                <Button
                  onClick={navigateNext}
                  textColor="white"
                  backgroundColor={COLORS.PRIMARY}
                >
                  Continue
                </Button>
              </div>
            )}

            <FailedLivenessModal
              opened={openedFailedLiveness}
              onClose={modalAction.close}
              onContinue={handleContinue}
              classes={classes}
            />
          </>
        )}

        {!documentState.isDocumentLoaded && (
          <Center maw={400} h={500} mx="auto">
            <Loader color="teal" />
          </Center>
        )}
      </div>
    </>
  );
};

export default DeclarationAccreditedInvestor;
